import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { EditableText } from "../components/Editable.js";
import { Marquee } from "../components/Marquee.js";
import { ScrollBackdrop } from "../components/PageBackdrop.js";
import { ReelScroller } from "../components/ReelScroller.js";
import { Reveal } from "../components/Reveal.js";
import { ReviewCard } from "../components/ReviewCard.js";
import { ErrorState, Spinner } from "../components/States.js";
import { Wordmark } from "../components/Wordmark.js";
import { HeroCanvas } from "../components/three/HeroCanvas.js";
import { usePageMeta } from "../hooks/usePageMeta.js";
import { useReducedMotion } from "../hooks/useReducedMotion.js";
import { api } from "../lib/api.js";
import { REEL_SLOT_ATTR } from "../components/three/reelGeometry.js";
import { canRenderStage } from "../lib/reelCapability.js";
import { site } from "../lib/site.js";
import type { Paginated, ReviewSummary } from "../lib/types.js";

/**
 * How many recent entries back the ticker.
 *
 * The API caps `pageSize` at 50, which is also comfortably more than the
 * ticker can show before it loops.
 */
const RECENT_SIZE = 50;

/** Written reviews shown under the feature. */
const LATEST_COUNT = 4;

/**
 * Reviews threaded onto the scroll reel.
 *
 * Each one costs most of a viewport of scrolling, so this is capped well below
 * the number of reviews that exist — the reel is an invitation to the archive,
 * not the archive itself.
 */
const REEL_COUNT = 6;

export function Home() {
  const [reviews, setReviews] = useState<ReviewSummary[] | null>(null);
  const [featured, setFeatured] = useState<ReviewSummary | null>(null);
  const [recent, setRecent] = useState<Paginated<ReviewSummary> | null>(null);
  const [reel, setReel] = useState<ReviewSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /** The backdrop below waits on this section's bottom edge. */
  const hero = useRef<HTMLElement>(null);
  /** The pinned inner panel — used to measure how far the dissolve has run. */
  const heroPin = useRef<HTMLDivElement>(null);
  /** The copy that actually dissolves. */
  const heroCopy = useRef<HTMLDivElement>(null);

  /**
   * Whether the reel is drawn on the shared fixed layer rather than in the
   * hero's own canvas.
   *
   * Same two conditions ReelScroller uses to decide whether to build the stage
   * at all, so the two cannot disagree and leave the hero with either no reel
   * or two. Settled during the first render, so the hero's right-hand column
   * never swaps its contents underneath the reader.
   */
  const reducedMotion = useReducedMotion();
  const [reelCapable] = useState(canRenderStage);
  const sharedReel = reelCapable && !reducedMotion;

  usePageMeta(
    site.brand,
    `${site.tagline} by ${site.author}, an independent filmmaker.`,
  );

  useEffect(() => {
    const controller = new AbortController();

    // Enough for the feature, the four latest and the reel, with room for the
    // feature to be an older review that would otherwise fall outside the page.
    api
      .listReviews(
        { pageSize: LATEST_COUNT + REEL_COUNT + 2, sort: "newest" },
        controller.signal,
      )
      .then((page) => setReviews(page.items))
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          cause instanceof Error ? cause.message : "Reviews could not be loaded.",
        );
      });

    return () => controller.abort();
  }, [reloadKey]);

  // Which review is pinned to the top is Davis's choice, not "whatever is
  // newest", so it is a question only the API can answer.
  useEffect(() => {
    const controller = new AbortController();

    api
      .getFeaturedReview(controller.signal)
      .then((result) => setFeatured(result.review))
      // The list below still renders; a failure here costs the hero card, not
      // the page.
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  // Separate call: the cards want written reviews, the ticker wants everything
  // that has been watched, quick rates included.
  useEffect(() => {
    const controller = new AbortController();

    api
      .listReviews(
        { kind: "all", sort: "newest", pageSize: RECENT_SIZE },
        controller.signal,
      )
      .then(setRecent)
      // The hero still works without these; a failure here is not worth an
      // error state over the whole page.
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  /**
   * The reel is a curated set, not a slice of the newest.
   *
   * Davis picks these in the editor. Asked for separately because the answer is
   * a different question from "the latest reviews" — a first-time visitor should
   * meet the six he would choose, not the six he happened to write most
   * recently.
   */
  useEffect(() => {
    const controller = new AbortController();

    api
      .listReviews(
        { onReel: true, sort: "newest", pageSize: REEL_COUNT },
        controller.signal,
      )
      .then((page) => setReel(page.items))
      // The reel is a flourish; the page below it is the substance.
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  /**
   * Dissolves the hero copy as the page scrolls through it.
   *
   * The hero is pinned for most of a viewport beyond its own height, and this
   * turns that extra scrolling into a fade rather than a slide — the copy sinks
   * and goes, in place, instead of travelling off the top of the screen. The
   * reel is deliberately untouched: it lives on a fixed layer of its own, so it
   * stays put while everything around it clears and then carries on into the
   * reel section, which is what makes it read as one continuous object.
   *
   * Skipped entirely when the hero is not pinned, which is the same condition
   * as the hero not having a reel — fading an ordinary section on scroll would
   * just be a hero that vanishes for no reason. Reduced motion is one of the
   * ways that happens, and `sharedReel` already accounts for it.
   */
  useEffect(() => {
    if (!sharedReel) return;

    const copy = heroCopy.current;
    const section = hero.current;
    if (!copy || !section) return;

    let queued = false;

    /**
     * The hero's top edge and its scrollable travel, as document offsets.
     *
     * Cached so the scroll handler needs nothing but `scrollY`. Reading a rect
     * per scroll frame forces a synchronous layout — cheap once, expensive
     * sixty times a second alongside two other handlers doing the same.
     */
    let sectionTop = 0;
    let travel = 0;

    const remeasure = () => {
      const rect = section.getBoundingClientRect();
      sectionTop = rect.top + window.scrollY;
      travel = rect.height - window.innerHeight;
    };

    const apply = () => {
      queued = false;
      if (travel <= 0) return;

      const t = Math.min(1, Math.max(0, (window.scrollY - sectionTop) / travel));
      // Most of the fade happens over the first two thirds, so the hero is
      // gone well before the reel section arrives rather than lingering.
      const linear = Math.min(1, t / 0.66);
      // Smoothstep. A linear ramp begins and ends at full speed, which reads as
      // the hero being switched off at both ends rather than leaving.
      const faded = linear * linear * (3 - 2 * linear);

      copy.style.opacity = String(1 - faded);
      // Sinks and shrinks a touch as it goes. Without the movement a pure
      // opacity fade reads as a rendering glitch rather than as an exit.
      copy.style.transform = `translate3d(0, ${faded * -40}px, 0) scale(${1 - faded * 0.05})`;
      // Once invisible it must stop swallowing clicks aimed at what is behind.
      copy.style.pointerEvents = faded > 0.9 ? "none" : "auto";
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    const onResize = () => {
      remeasure();
      onScroll();
    };

    remeasure();
    apply();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [sharedReel]);

  /**
   * The feature, then the newest four that are not it.
   *
   * Filtering by id rather than slicing past the first entry: the featured
   * review is whichever one Davis pinned, so it is not necessarily the newest
   * and may sit anywhere in this list — or, if it is an older piece, outside it
   * entirely.
   */
  const { hero: heroReview, latest } = useMemo(() => {
    const items = reviews ?? [];
    const pinned = featured ?? items[0] ?? null;
    const rest = items.filter((item) => item.id !== pinned?.id);

    return {
      hero: pinned,
      latest: rest.slice(0, LATEST_COUNT),
    };
  }, [reviews, featured]);

  /**
   * What actually goes on the reel.
   *
   * Davis's picks when he has made any. The newest few are the fallback for a
   * site where nobody has curated it yet — an empty reel on the home page would
   * be worse than an unconsidered one.
   */
  const reelReviews = useMemo(() => {
    if (reel && reel.length > 0) return reel;
    return (reviews ?? []).slice(0, REEL_COUNT);
  }, [reel, reviews]);

  return (
    <>
      {/*
        Waits behind the hero and fades in as it leaves. `veil` rather than
        `scene`: the reel section sits directly on top of it, and the HAL
        corridor has enough hard red detail to fight the film cells for
        attention at the lighter setting.
      */}
      <ScrollBackdrop name="odyssey" intensity="veil" after={hero} />

      {/*
        The hero.

        Taller than the viewport, with its contents pinned inside it, so that
        scrolling through that extra height dissolves the hero in place rather
        than sliding it off the top. The reel is on a fixed layer behind all of
        this and is *not* faded — it stays while the copy around it goes, which
        is what makes it read as the same wheel carrying on into the reel
        section rather than as one thing leaving and another arriving.

        Pinned whenever the reel is on the page, at every width — including a
        phone, where the hero stacks into one column and the reel takes a slot
        under the copy. It has to be: the reel is drawn on a canvas fixed to the
        viewport, so it can only sit in a slot that is itself holding still. An
        unpinned hero would scroll its slot out from under a reel that stayed.

        Not pinned when there is no reel — reduced motion, or a machine without
        WebGL. There the hero is an ordinary section and always was; pinning it
        would buy the reader most of an extra screen of scrolling to look at a
        hero that has nothing to do while they scroll it.

        `svh`/`dvh` rather than `vh`: on a phone `100vh` is the viewport with
        the browser's chrome hidden, which is not the viewport you have while
        you are scrolling through it.

        No background colour, either — an opaque one would paint over the reel
        layer behind it. `body` is already ink-900, so nothing looks different.
      */}
      <section
        ref={hero}
        className={`grain relative ${
          sharedReel ? "h-[175svh]" : "border-b border-ink-700"
        }`}
      >
        <div
          ref={heroPin}
          className={`relative overflow-hidden ${
            sharedReel ? "sticky top-0 h-dvh" : ""
          }`}
        >
          {/* Warm pool of light behind the type, anchored to the reel's side. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              // Mixed from the token, not a literal rgba, so the glow follows
              // --color-lamp-400 if the accent is ever retuned.
              background:
                "radial-gradient(60rem 40rem at 78% 32%, color-mix(in oklab, var(--color-lamp-400) 14%, transparent), transparent 68%)",
            }}
          />

          {/*
            Everything that dissolves — the copy and the ticker together, so
            they leave as one thing. The reel is not in here: it is on the fixed
            layer behind the page, so it survives the fade and carries on into
            the reel section.

            A column at `lg`, so the copy can take the space it needs and the
            ticker sits on the bottom edge of the pinned viewport.
          */}
          <div
            ref={heroCopy}
            className={`relative ${sharedReel ? "flex h-full flex-col" : ""}`}
          >
            <div
              className={`mx-auto w-full max-w-6xl px-5 ${
                sharedReel
                  ? // `pb-20` clears the ticker, which is pinned to the bottom
                    // edge of this panel and out of flow, so the grid above has
                    // no other way to know it is there. At `pb-10` the reel ran
                    // a couple of dozen pixels under the moving titles.
                    "flex min-h-0 flex-1 flex-col justify-center pb-20 pt-20 lg:pt-0"
                  : "pb-10 pt-14 sm:pt-20"
              }`}
            >
            {/*
              One column on a phone, two from `lg`. The row template is what
              keeps a pinned mobile hero honest: the copy takes the height it
              needs and the reel's slot takes whatever is left, down to nothing,
              so the hero fits a short handset instead of overflowing it.
            */}
            <div
              className={`grid items-center gap-5 sm:gap-8 lg:grid-cols-[1.25fr_1fr] lg:grid-rows-1 ${
                sharedReel ? "min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)]" : ""
              }`}
            >
            <div>
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.7rem] uppercase tracking-[0.32em] text-lamp-400">
                {site.brand}
                <span aria-hidden="true" className="text-ink-500">
                  ✳
                </span>
                <span className="text-paper-500">
                  <EditableText id="home.eyebrow">
                    Watching since {site.watchingSince}
                  </EditableText>
                </span>
              </p>

              {/*
                The same lockup the header uses, at poster scale. The section
                clips overflow, so the descender on "g" and the tight measure
                never push the page into a horizontal scroll.
              */}
              <h1 className="mt-6">
                <Wordmark size="heroStack" words="stacked" />
              </h1>

              <p className="mt-7 max-w-md text-lg leading-relaxed text-paper-200">
                <EditableText id="home.tagline">
                  Real reactions from {site.author} — a filmmaker who watches far
                  too much, says what he actually thinks, and rates out of ten.
                </EditableText>
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/reviews"
                  className="rounded-sm bg-lamp-400 px-5 py-2.5 text-sm font-semibold text-ink-950 transition-transform hover:-translate-y-0.5 hover:bg-lamp-300"
                >
                  <EditableText id="home.cta-reviews" pencil="top-left">
                    Read the reviews
                  </EditableText>
                </Link>
                <Link
                  to="/ratings"
                  className="rounded-sm border border-ink-600 px-5 py-2.5 text-sm font-semibold text-paper-200 transition-colors hover:border-lamp-400 hover:text-lamp-400"
                >
                  <EditableText id="home.cta-ratings" pencil="top-left">
                    See every rating
                  </EditableText>
                </Link>
              </div>
            </div>

            {/*
              `self-stretch` is load-bearing. The grid is `items-center`, which
              makes every cell take its content's height — and the slot below
              sizes itself *from* that height, so the two defined each other and
              settled on zero. Stretching this cell to the row gives the slot a
              definite height to be square against.
            */}
            <div className="flex min-h-0 justify-center self-stretch lg:justify-end lg:self-center">
              {/*
                When the 3D reel is available it is drawn on the shared fixed
                layer, over this slot — so all that is needed here is something
                the right size to hold the column open. Rendering HeroCanvas as
                well would put a second, unrelated reel on the page.

                `REEL_SLOT_ATTR` is how the reel finds it. The canvas is fixed
                to the viewport and has no idea where the hero put its slot, and
                the world position it used to be given was measured against the
                two-column layout — 1.62 units right of centre, which on a
                393px-wide phone is the whole reel off the side of the screen.
                Measuring the slot instead is right at every width.

                Sized by height below `lg` and by width from `lg` up: in one
                column the slot gets whatever vertical space the copy has not
                taken, and a square of that height can never be wider than a
                phone. In two columns it fills its own column as it always did.
              */}
              {sharedReel ? (
                <div
                  aria-hidden="true"
                  {...{ [REEL_SLOT_ATTR]: "" }}
                  className="aspect-square h-full max-h-full min-h-[5rem] max-w-full lg:h-auto lg:min-h-0 lg:w-full lg:max-w-[26rem]"
                />
              ) : (
                <HeroCanvas />
              )}
            </div>
            </div>
            </div>

            {/*
              The ticker, on the bottom edge of the landing rather than in a
              band below it. It is part of what you arrive on, and it leaves
              with the rest of the hero instead of surviving as a lone strip
              sliding over the reel.

              Pinned to the bottom rather than laid out after the copy: the
              hero's own content can fill a short viewport on its own, and in
              normal flow the ticker was simply pushed past the bottom edge and
              clipped away by the panel's `overflow-hidden`.
            */}
            {recent && (
              <div
                className={
                  sharedReel ? "absolute inset-x-0 bottom-0" : "lg:absolute lg:inset-x-0 lg:bottom-0"
                }
              >
                <Marquee items={recent.items} />
              </div>
            )}
          </div>
        </div>
      </section>

      {reelReviews.length > 0 && <ReelScroller reviews={reelReviews} />}

      <div className="relative mx-auto max-w-6xl px-5 py-16">
        {error && (
          <ErrorState
            message={error}
            onRetry={() => {
              setError(null);
              setReviews(null);
              setReloadKey((key) => key + 1);
            }}
          />
        )}

        {!error && reviews === null && <Spinner label="Loading reviews" />}

        {!error && reviews !== null && reviews.length === 0 && (
          <p className="py-16 text-center text-paper-400">
            The first review is on its way. Check back soon.
          </p>
        )}

        {heroReview && (
          <Reveal>
            <section aria-labelledby="featured-heading">
              <h2
                id="featured-heading"
                className="mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-paper-500"
              >
                <span className="h-px w-8 bg-lamp-400" aria-hidden="true" />
                Featured review
              </h2>

              <ReviewCard review={heroReview} variant="feature" />
            </section>
          </Reveal>
        )}

        {latest.length > 0 && (
          <section aria-labelledby="latest-heading" className="mt-20">
            <Reveal>
              <div className="mb-8 flex items-baseline justify-between">
                <h2
                  id="latest-heading"
                  className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-paper-500"
                >
                  <span className="h-px w-8 bg-lamp-400" aria-hidden="true" />
                  Latest reviews
                </h2>
                <Link
                  to="/reviews"
                  className="inline-block py-1.5 text-sm text-lamp-400 underline-offset-4 hover:underline"
                >
                  View all
                </Link>
              </div>
            </Reveal>

            <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
              {latest.map((review, index) => (
                // Stagger caps at the row length so the last card in a long
                // list is not left waiting most of a second to appear.
                <Reveal key={review.id} delay={(index % 4) * 90}>
                  <ReviewCard review={review} />
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
